import Constants from '@/Constants';
import { useMetadata } from '@/hooks/useMetadata';
import { AvailableTranslation } from '@/models/AvailableTranslation';
import { ContentMetadata } from '@/models/ContentMetadata';
import Item from '@/models/Item';
import { LanguageType } from '@/models/LanguageType';
import { LinkMetadata } from '@/models/LinkMetadata';
import { contentService } from '@/services/ContentService';
import { ApplicationState } from '@/store';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import './style.scss';

interface Props {
    content: Item;
    metadata: ContentMetadata;
}

const ExternalPlayer: React.FC<Props> = (props: Props) => {
    const { content, metadata } = props;

    const textSizeLimit: number = 240;

    const { strings, currentContentLanguage } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
        currentContentLanguage: state.locale.currentContentLanguage || (state.locale.data._LANGUAGE as LanguageType),
    }));

    const setCurrentPath = useCallback((): string => {
        if (content) {
            if (currentContentLanguage && currentContentLanguage.length > 0) {
                return contentService.getContentPathByLanguageType(content, currentContentLanguage);
            }
        }
        return '';
    }, [content, currentContentLanguage]);

    const [currentContentPath, setCurrentContentPath] = useState<string>(setCurrentPath);
    const [currentContentTranslation, setCurrentContentTranslation] = useState<AvailableTranslation>();
    const [fullText, setFullText] = useState<boolean>(false);

    const { metadata: linkMetadata } = useMetadata(currentContentPath);

    const getIcon = (type: string): string => {
        switch (type) {
            case strings.AUDIO_TYPE_NAME:
                return Constants.icons.MICROPHONE;
            case strings.VIDEO_TYPE_NAME:
                return Constants.icons.VIDEOCAM;
            case strings.EXTERNAL_LINK:
                return Constants.icons.LINK;
            default:
                return Constants.icons.FILE_DOCUMENT_OUTLINE;
        }
    };

    const getDescription = (linkMetadata: LinkMetadata, currentContentPath: string, contentTranslation: AvailableTranslation): string => {
        if (linkMetadata && linkMetadata.description) {
            return linkMetadata.description;
        } else if (contentTranslation && contentTranslation.description) {
            return contentTranslation.description;
        } else {
            return currentContentPath;
        }
    };

    const loadNewPathByNewLanguage = useCallback(() => {
        if (content && currentContentLanguage) {
            setCurrentContentPath(contentService.getContentPathByLanguageType(content, currentContentLanguage));
            setCurrentContentTranslation(contentService.getContentTranslationByLanguageType(content, currentContentLanguage));
        }
    }, [currentContentLanguage]);

    useEffect(loadNewPathByNewLanguage, [currentContentLanguage]);

    const fullTextController = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>): void => {
        e.preventDefault();
        setFullText(!fullText);
    };

    const textLimiter = (str: string): string[] => {
        const newStr: string[] = [];
        if (!fullText && textSizeLimit < str.length) {
            newStr.push(str.substr(0, textSizeLimit));
            newStr[newStr.length - 1] === ' ' && newStr.pop();
            newStr.push('... ');
            return newStr;
        }
        newStr.push(str);
        return newStr;
    };

    const description: string = getDescription(linkMetadata as LinkMetadata, currentContentPath, currentContentTranslation as AvailableTranslation);

    return (
        <a className="external-player-link" href={content.path} rel="noopener noreferrer" target="_blank">
            <div className="box-link">
                <div className="box-link__cover">
                    {linkMetadata && linkMetadata.image && <img alt="" className="box-link__cover-thumb" src={linkMetadata.image} />}
                    <div className="box-link__cover-overlay"></div>
                    <div className="box-link__cover-icon">
                        <span className="material-icons-outlined">{getIcon(metadata.typeName)}</span>
                    </div>
                </div>
                <div className="box-link__content-resume">
                    <p className="box-link__content-resume-description">
                        {textLimiter(description)}
                        {!fullText && textSizeLimit < description.length && (
                            <span className="description see-more" onClick={(e) => fullTextController(e)}>
                                {strings.READ_MORE}
                            </span>
                        )}
                    </p>
                </div>
            </div>
        </a>
    );
};

export default ExternalPlayer;
